import { SettlementAccountStatus } from "types/stripe";

// What the merchant should be shown next, given a settlement account status.
//
// Pure and shared on purpose. The same decision is made at FOUR call sites —
// redeem click, redeem's ?stripe_return, settings click, settings'
// ?stripe_return — which previously each re-derived it from raw status fields
// and had already drifted apart. Deciding here and rendering per-flow keeps the
// click path and the return path of a single flow in agreement.
export type SettlementRoute =
  // Payouts are live: redeem (balance CTA) or "you're all set" (settings).
  | { kind: "ready" }
  // Stripe merchant with no Connect account yet — create it, then re-route.
  | { kind: "create_stripe_account" }
  // Column collects bank details in-app; no hosted KYC.
  | { kind: "column_form" }
  // Send the merchant to Stripe-hosted onboarding.
  //  - "required": payouts are NOT live until this is done.
  //  - "optional": payouts work today, but Stripe wants more before a deadline.
  | { kind: "onboarding"; url: string; variant: OnboardingVariant }
  // Stripe is verifying what was submitted. Nobody can speed this up.
  | { kind: "pending_review" }
  // Rejected/listed. An account link cannot clear this — only the issuer can help.
  | { kind: "blocked" }
  // Status we can't map. Surfaced as an error rather than thrown, so a shape we
  // didn't anticipate degrades to a message instead of an unhandled rejection.
  | { kind: "unknown" };

export type OnboardingVariant = "required" | "optional";

export function resolveSettlementRoute(
  status: SettlementAccountStatus,
): SettlementRoute {
  // Column never sends `action` — its status is just hasAccount/payoutsEnabled,
  // and an account there is payouts-ready the moment it exists. Keep it on the
  // pre-`action` logic; only Stripe gained the state machine.
  if (status.platformId === "column") {
    return status.payoutsEnabled ? { kind: "ready" } : { kind: "column_form" };
  }

  // Stripe, no account yet: also `action`-less (there's no Stripe account to
  // report on). The caller creates one and routes the resulting status.
  if (!status.hasAccount) {
    return { kind: "create_stripe_account" };
  }

  switch (status.action) {
    case "none":
      return { kind: "ready" };
    case "blocked":
      return { kind: "blocked" };
    case "pending_review":
      return { kind: "pending_review" };
    case "onboarding":
      return status.onboardingUrl
        ? {
            kind: "onboarding",
            url: status.onboardingUrl,
            variant: "required",
          }
        : { kind: "unknown" };
    case "action_required":
      // Payouts are live, so a missing link is not a dead end — the merchant can
      // still transact. Fall through to "ready" rather than blocking them on our
      // inability to offer the optional step.
      return status.onboardingUrl
        ? { kind: "onboarding", url: status.onboardingUrl, variant: "optional" }
        : { kind: "ready" };
    default:
      // `action` is undefined: a backend that predates it. Fall back to the
      // fields that have always been there, so an un-deployed server degrades to
      // the previous behaviour instead of dead-ending every merchant.
      if (status.payoutsEnabled) return { kind: "ready" };
      if (status.onboardingUrl) {
        return {
          kind: "onboarding",
          url: status.onboardingUrl,
          variant: "required",
        };
      }
      return { kind: "unknown" };
  }
}

export const BLOCKED_ACCOUNT_MESSAGE =
  "Your payout account has been blocked and can no longer be used. " +
  "Completing setup again won't clear this — please contact your issuer.";

export const UNKNOWN_STATUS_MESSAGE =
  "We couldn't determine your payout account status. Please try again, or " +
  "contact your issuer if this keeps happening.";
